import type { LabTestStatus } from "@/models/LabTestStatus";
import type { EquipmentUsedEntry } from "./equipmentUsed";
import type { StateTransitionContract } from "./stateTransitions";

export interface LaboratoryInfo {
  id: string;
  name: string | null;
}

/**
 * Physical container the sample arrived in. Declared here rather than in `common/` so
 * `scripts/generate-template-docs` resolves the allowed values for report authors.
 * Canonical values match the backend `SampleType` enum; the generated import template
 * writes them capitalised and `mapSampleType` parses them back, so they are not localised.
 */
export const SAMPLE_TYPES = [
  "bag",
  "wax",
  "liner",
  "tube",
  "jar",
  "quart",
  "bucket",
  "batch",
  "block",
  "core",
] as const;

export type SampleType = (typeof SAMPLE_TYPES)[number];

export interface SampleInfo {
  id: string;
  fugroSampleId: string;
  name: string;
  topDepthMeters?: number;
  location?: string;
  condition?: "disturbed" | "undisturbed" | null;
  type?: SampleType | null;
}

export interface ProjectInfo {
  id: string;
  goldenRecordId: string;
  name: string;
  contract?: string;
  client?: string | null;
}

export interface LabTestMetadata {
  id: string;
  type: string;
  method: string;
  status: LabTestStatus;
  createdAt: string;
  processingTestId?: string | null;
  isRetest?: boolean;
  stateTransition?: StateTransitionContract;
  laboratory: LaboratoryInfo;
  sample?: SampleInfo;
  project?: ProjectInfo;
  equipmentUsed?: EquipmentUsedEntry[];
  displayUnits?: Record<string, string>;
}

export const asDisplayUnits = (chosen: unknown): Record<string, string> =>
  typeof chosen === "object" && chosen !== null
    ? Object.fromEntries(Object.entries(chosen).filter(([, unitId]) => typeof unitId === "string"))
    : {};

export const readPersistedDisplayUnits = (data: Record<string, unknown> | null): Record<string, string> =>
  asDisplayUnits(data?.displayUnits);
